// UPPDRAG-32 — pure helper for the FeatureRoute decision tree.
//
// Pulled out of App.tsx's FeatureRoute component so vitest can guard
// the gate logic without standing up a router + auth + org context.
//
// The decision is one of three states:
//   - loading: useFeatureFlags is still fetching → render nothing
//     (the loader is shown by parent layout)
//   - allowed: feature is enabled for the current org → render children
//   - redirect: feature is disabled → <Navigate to={to}> + toast.error(message)
//
// The redirect target differs per surface:
//   - manager routes (admin) → /manager/dashboard so admin lands on a
//     surface they always have access to
//   - player routes → / (homepage)
//
// Toast message is hard-coded Swedish per Rickard's preference for
// admin-facing strings (matches inline-fallback pattern used in
// MoveBookingDialog).

export type FeatureGateDecision =
  | { type: "loading" }
  | { type: "allowed" }
  | { type: "redirect"; to: string; toastMessage: string };

export interface FeatureGateArgs {
  /** Are the org's feature flags still loading from the server? */
  isLoading: boolean;
  /** Is this specific feature enabled for the current org? */
  isEnabled: boolean;
  /** Path to redirect to if the feature is disabled. Defaults to "/". */
  redirectTo?: string;
  /** Toast message to show on redirect. Defaults to a Swedish string. */
  toastMessage?: string;
}

const DEFAULT_REDIRECT = "/";
const DEFAULT_TOAST = "Denna funktion är inte aktiverad för din klubb";

/**
 * Pure decision function for FeatureRoute. Caller renders + fires the
 * toast based on the returned decision.
 */
export function decideFeatureGate(args: FeatureGateArgs): FeatureGateDecision {
  if (args.isLoading) return { type: "loading" };
  if (!args.isEnabled) {
    return {
      type: "redirect",
      to: args.redirectTo ?? DEFAULT_REDIRECT,
      toastMessage: args.toastMessage ?? DEFAULT_TOAST,
    };
  }
  return { type: "allowed" };
}
